import React, { useMemo } from 'react';
import type { ScoreHistoryEntry, ScoreHistoryGroup } from '../../types';
import './ScoreChart.css';

interface Props {
  history: ScoreHistoryEntry[];
  groups: ScoreHistoryGroup[];
}

const PADDING = { top: 24, right: 24, bottom: 110, left: 64 };
const HEIGHT = 360;

export default function ScoreChart({ history, groups }: Props) {
  const { maxScore, width } = useMemo(() => {
    const allScores = history.flatMap((h) => Object.values(h.scores));
    const maxScore = Math.max(0, ...allScores);
    const minWidth = 480;
    const width = Math.max(minWidth, history.length * 80 + PADDING.left + PADDING.right);
    return { maxScore, width };
  }, [history]);

  if (history.length === 0) {
    return <p className="text-muted">Aucune donnée de score disponible.</p>;
  }

  const innerW = width - PADDING.left - PADDING.right;
  const innerH = HEIGHT - PADDING.top - PADDING.bottom;

  const xStep = history.length > 1 ? innerW / (history.length - 1) : innerW / 2;
  const yScale = (v: number) => maxScore > 0 ? innerH - (v / maxScore) * innerH : innerH;

  const tickCount = 5;
  const ticks = Array.from({ length: tickCount + 1 }, (_, i) =>
    Math.round((maxScore / tickCount) * i)
  );

  // Truncate label for x-axis display (full label visible via tooltip)
  const truncLabel = (label: string) => label.length > 22 ? label.slice(0, 21) + '…' : label;

  return (
    <div className="score-chart-wrap">
      <svg
        width={width}
        height={HEIGHT}
        style={{ display: 'block', minWidth: 360 }}
        aria-label="Évolution des scores"
      >
        {/* Y-axis grid + labels */}
        {ticks.map((tick) => {
          const y = PADDING.top + yScale(tick);
          return (
            <g key={tick}>
              <line
                x1={PADDING.left} x2={PADDING.left + innerW}
                y1={y} y2={y}
                stroke="#2d2d4e" strokeWidth={1}
              />
              <text
                x={PADDING.left - 8} y={y + 5}
                textAnchor="end" fontSize={12} fill="#94a3b8" fontWeight={600}
              >
                {tick}
              </text>
            </g>
          );
        })}

        {/* X-axis labels — rotated -50°, full label as title tooltip */}
        {history.map((h, i) => {
          const x = PADDING.left + i * xStep;
          const label = truncLabel(h.label);
          return (
            <g key={i}>
              <text
                x={x}
                y={HEIGHT - PADDING.bottom + 18}
                textAnchor="end"
                fontSize={11}
                fill="#94a3b8"
                transform={`rotate(-50, ${x}, ${HEIGHT - PADDING.bottom + 18})`}
              >
                {label}
                <title>{h.label}</title>
              </text>
            </g>
          );
        })}

        {/* Lines and dots per group */}
        {groups.map((group) => {
          const linePoints = history.map((h, i) => {
            const score = h.scores[group.id] ?? 0;
            const x = PADDING.left + i * xStep;
            const y = PADDING.top + yScale(score);
            return `${x},${y}`;
          });

          const dotPoints = history.map((h, i) => ({
            x: PADDING.left + i * xStep,
            y: PADDING.top + yScale(h.scores[group.id] ?? 0),
            score: h.scores[group.id] ?? 0,
          }));

          return (
            <g key={group.id}>
              <polyline
                points={linePoints.join(' ')}
                fill="none"
                stroke={group.color}
                strokeWidth={3}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {dotPoints.map((pt, i) => (
                <circle
                  key={i}
                  cx={pt.x} cy={pt.y} r={5}
                  fill={group.color}
                  stroke="#0f0f1a"
                  strokeWidth={2}
                >
                  <title>{group.name}: {pt.score} pts — {history[i].label}</title>
                </circle>
              ))}
            </g>
          );
        })}

        {/* Axes */}
        <line
          x1={PADDING.left} x2={PADDING.left}
          y1={PADDING.top} y2={PADDING.top + innerH}
          stroke="#4b5563" strokeWidth={2}
        />
        <line
          x1={PADDING.left} x2={PADDING.left + innerW}
          y1={PADDING.top + innerH} y2={PADDING.top + innerH}
          stroke="#4b5563" strokeWidth={2}
        />
      </svg>

      {/* Legend */}
      <div className="chart-legend">
        {groups.map((g) => (
          <span key={g.id} className="chart-legend-item">
            <span className="chart-legend-dot" style={{ background: g.color }} />
            {g.name}
          </span>
        ))}
      </div>
    </div>
  );
}
